/**
 * Typed route manifest for the application.
 *
 * One entry per route. Each entry has:
 *   path    — the react-router template (`:param` for dynamic segments)
 *   feature — the feature this route belongs to (see below)
 *   params  — a typed path builder, `paths.foo({ id })` returning a string
 *
 * Call sites import `paths` and build URLs via `paths.customerDetail({ id })`
 * instead of hand-rolled template literals. `routes.*.path` is passed to
 * `createBrowserRouter` in the web shell so the template and the builder
 * cannot drift.
 *
 * A shared `<RouteLink>` / `navigate()` layer is deliberately NOT part of
 * this manifest — stack semantics and link affordances differ between
 * web (history + anchors) and native (navigation containers + Pressable),
 * and pre-designing them without a native consumer invites the wrong
 * abstraction. When a native shell exists, it reuses this manifest and
 * wires its own navigation adapter on top.
 *
 * ## Why every route names a feature
 *
 * Production is a deliberately narrow slice of this application. The feature
 * mechanism's default is that unreferenced code is visible everywhere —
 * right for landing new work dark, wrong when most of the app is not
 * production-ready and a page added next month would be live unless someone
 * remembered to hide it.
 *
 * `feature` is therefore REQUIRED and NON-NULLABLE, enforced by the
 * `satisfies` clause below: a new route without one is a compile error, and a
 * misspelled one is too, because `FeatureName` is a union of the names
 * declared in `modules/features.yaml`. That converts "forgot to think about
 * it" into "cannot proceed without deciding", which is the property the whole
 * design rests on — the manifest is already the complete inventory of the
 * application, so total coverage here means nothing can be live in production
 * by omission.
 *
 * Note the deliberate asymmetry: the manifest never mentions environments and
 * the declaration never mentions routes. A feature spans several pages,
 * backend routers and jobs; if enablement lived on each route, catalogue's
 * nine routes would repeat it and could drift.
 *
 * See docs/features/staging-environment/05_production_surface_spec.md §4.
 */

import { isFeatureEnabled, type FeatureName } from "../config/features";
import type { OrderRailStep } from "../domain/order-lifecycle-rail";
import type { QuoteRailStep } from "../domain/quote-presentation";
import type { PlannedTabKey } from "../domain/planned-sections";

interface RouteDefinition {
  readonly path: string;
  readonly feature: FeatureName;
}

// Main application area — all routes are top-level (no /dashboard prefix).
/**
 * The supplier page's tabs, in order; Now is the default. No Pricing tab:
 * supplier pricing belongs to the pricing module (Ben, 4 Oct). Integration is
 * the supplier interface's (mock-up 12), shown where supplier_portal is on.
 */
export const SUPPLIER_TABS = [
  "now",
  "purchase-orders",
  "purchase-invoices",
  "coding",
  "integration",
] as const;
export type SupplierTab = (typeof SUPPLIER_TABS)[number];

/** The Ledger rules page's tabs: Coding, then Spread over (Ben, 3 Oct; renamed from Prepaid 30 Oct). */
export const LEDGER_RULES_TABS = [
  "coding",
  "spread-over",
  "sales-coding",
] as const;
export type LedgerRulesTab = (typeof LEDGER_RULES_TABS)[number];

/** Finance's Aged debt page: its first tab is aged debt itself, the second the disputes (`/finance/aged-debt/disputes`). */
export const AGED_DEBT_TABS = ["aged-debt", "disputes"] as const;
export type AgedDebtTab = (typeof AGED_DEBT_TABS)[number];

export const routes = {
  root: {
    path: "/",
    feature: "sales_home",
  },
  // One's generic object pages (docs/features/object-ui-and-skills/04_frontend_spec.md
  // §6.1): every declared object's list and record, by its qualified id
  // (`/objects/orders.order/ORD-K48193`). The list's state, a queue included,
  // rides in the query string so a skill can link to it.
  objectList: {
    path: "objects/:objectId",
    feature: "one",
  },
  objectRecord: {
    path: "objects/:objectId/:recordKey",
    feature: "one",
  },
  // Find's results (One feedback FB-055): what Enter in Find opens when no record is highlighted, every searchable
  // object's matches for `?q=`, or a plain "nothing matched", with each list searched one click away.
  find: {
    path: "find",
    feature: "one",
  },
  customers: {
    path: "customers",
    feature: "customers",
  },
  // Create a customer in CIS (v1 completion C5). Its own feature rather than
  // `customers`: the Customers page is live in production and this page is not
  // until `v1_completion` is. A static segment, so it ranks above `:customerId`.
  customerCreate: {
    path: "customers/new",
    feature: "v1_completion",
  },
  customerDetail: {
    path: "customers/:customerId",
    feature: "customers",
  },
  // The customer's Orders tab: the orders list scoped to this customer,
  // inside the customer's frame (order to invoice).
  customerOrders: {
    path: "customers/:customerId/orders",
    feature: "orders",
  },
  // The customer's Orders tab, All history: its CIS orders and Tharstern work in one list, each Tharstern row with
  // Order again (v1 completion F5).
  customerOrderHistory: {
    path: "customers/:customerId/orders/history",
    feature: "orders",
  },
  // The customer's Quotes tab: the Quotes list scoped to this customer
  // (CIS quotes only; Tharstern's estimates stay the Estimates tab).
  customerQuotes: {
    path: "customers/:customerId/quotes",
    feature: "quotes",
  },
  // The customer's Settings tab: how its payer pays, inside the customer's
  // frame. Credit control owns the page (docs/features/credit-control/
  // 06_ui_strategy.md decision 10); every staff role reads it.
  customerSettings: {
    path: "customers/:customerId/settings",
    feature: "customers",
  },
  // The customer's Invoices tab (order to invoice doc 50 §9 item 4): its
  // pro-forma, invoices and credit notes, inside the customer's frame.
  customerInvoices: {
    path: "customers/:customerId/invoices",
    feature: "sales_ledger",
  },
  // A planned customer tab (PLANNED_SECTIONS): the customer's frame around a
  // "not built yet" line. Only while `previews` is on.
  customerPlanned: {
    path: "customers/:customerId/planned/:section",
    feature: "previews",
  },
  // Credit control's list of billing accounts and the import (docs/features/
  // credit-control/06_ui_strategy.md). Finance data about customers, gated by
  // the credit_control:view permit rather than a feature of its own, so it
  // rides the customers feature.
  // The test console (docs/features/quote-to-invoice-e2e-testing/07 §1, one route per tab): a testing preview's scenarios,
  // fast-forward, outbox and simulated outside parties. The feature is on in `testing` only; the nav entry
  // also needs journey_testing:use.
  journeyTesting: {
    path: "testing/:tab",
    feature: "journey_testing",
  },
  // `/testing` alone: the console on its first tab (Scenarios).
  journeyTestingHome: {
    path: "testing",
    feature: "journey_testing",
  },
  // Opens a testing preview by its environment id (06 §4.1) from the main app: mints the one-time exchange with
  // the signed-in user's token and moves the browser to the preview. The share URL `cis preview` prints.
  testingPreviewOpen: {
    path: "previews/testing/:environmentId",
    feature: "dev_tooling",
  },
  creditControl: {
    path: "credit-control",
    feature: "customers",
  },
  tickets: {
    path: "tickets",
    feature: "tickets",
  },
  ticketDetail: {
    path: "tickets/:id",
    feature: "tickets",
  },
  alerts: {
    path: "alerts",
    feature: "recommendations",
  },
  recommendations: {
    path: "recommendations",
    feature: "recommendations",
  },
  notes: {
    path: "notes",
    feature: "tickets",
  },
  communications: {
    path: "communications",
    feature: "communications",
  },
  prospects: {
    path: "prospects",
    feature: "prospects",
  },
  leads: {
    path: "leads",
    feature: "leads",
  },
  leadDetail: {
    path: "leads/:leadId",
    feature: "leads",
  },
  estimates: {
    path: "estimates",
    feature: "estimates_view",
  },
  commercialExceptions: {
    path: "commercial-exceptions",
    feature: "commercial_exceptions",
  },
  // Courier invoice upload, carriage-cost exceptions and bucket jobs. Sits
  // beside the exceptions board as the second Commercial leaf.
  carriageCosts: {
    path: "commercial/carriage",
    feature: "carriage_cost_capture",
  },
  settingsPricingTargets: {
    path: "settings/pricing-targets",
    feature: "commercial_exceptions",
  },
  settingsPricingReview: {
    path: "settings/pricing-review",
    feature: "catalogue",
  },
  estimateDetail: {
    path: "estimates/:id",
    feature: "estimates_view",
  },
  // The estimate page's tabs beyond Now (ESTIMATE_TABS): one address per tab,
  // so a tab can be linked and returned to. `estimates/tharstern/:id` is a
  // static sibling and outranks this.
  estimateTab: {
    path: "estimates/:id/:tab",
    feature: "estimates_view",
  },
  // The catalogue work temporarily split estimate detail in two — Tharstern
  // detail here, a catalogue-only view on `estimates/:id`. That split is gone:
  // one page now serves all three origins and `estimates/:id` is canonical
  // again. This entry exists only so the URLs that went out during the split
  // (bookmarks, links pasted into Teams) still land somewhere; the shell
  // registers it as a redirect, not a page.
  estimateDetailLegacyTharstern: {
    path: "estimates/tharstern/:id",
    feature: "estimates_view",
  },
  createEstimate: {
    path: "estimates/new",
    feature: "estimates_create",
  },
  // Product catalogue — the simplified "menu" ordering journey. The grid
  // uses URL customer context separately from product filters; the item route
  // opens the configurator in "use" mode.
  catalogue: {
    path: "catalogue",
    feature: "catalogue",
  },
  dictionaryWorkbook: {
    path: "catalogue/import",
    feature: "catalogue",
  },
  catalogueItem: {
    path: "catalogue/:itemId",
    feature: "catalogue",
  },
  // Item-level market intelligence — competitor prices for an item, pinned by
  // spec with quantity as the free axis. Shared by every price group's stance.
  // Every catalogue-curation surface below moved under Settings › Catalogue
  // (product-owner decision, 12 Sep,
  // docs/features/cost-engine-productionisation/03_manufacturing_surfaces_design.md
  // "IA — Manufacturing nests under Settings › Catalogue as Live costing"):
  // the Dictionary page used to sit outside SettingsLayout,
  // which is why the settings tab bar vanished on them, and Manufacturing only
  // ever existed as a by-product of catalogue live pricing. The old
  // `catalogue/...` and `settings/manufacturing/...` paths are kept below as
  // pure redirect sources so bookmarks and pasted links keep resolving.

  // Dictionary admin surface (design doc
  // docs/features/product-catalogue/48_dictionary_admin_surface_design.md) —
  // costing is dictionary-grain since the rewire; this is the dictionary-
  // grain index/detail that replaces the old catalogue-item-grain "Variant
  // costs" tab as the entry point into variant costs. Static segments rank
  // above dynamic ones in react-router v6, so "dictionary" here never
  // collides with `catalogueItem`'s `:itemId`.
  dictionaryAdmin: {
    path: "settings/catalogue/dictionary",
    feature: "catalogue",
  },
  // The dictionary gap log (v1 completion E): every use of work the dictionary
  // could not describe, for the people who extend it. The static "gap-log"
  // segment ranks above `dictionaryItemAdmin`'s `:dictionaryItemId`.
  dictionaryGapLog: {
    path: "settings/catalogue/dictionary/gap-log",
    feature: "catalogue",
  },
  dictionaryItemAdmin: {
    path: "settings/catalogue/dictionary/:dictionaryItemId",
    feature: "catalogue",
  },
  // Cost-pair drill-down (design doc 48's routed replacement for the old
  // cramped CostPairDrillDown modal) — one (variant, quantity) cell's full
  // construction, deep-linkable for SME price-review feedback. Nested under
  // dictionaryItemAdmin's own path so a bookmark's parent segment is
  // self-evident; "pair" disambiguates from a future per-variant-only route.
  dictionaryCostPair: {
    path: "settings/catalogue/dictionary/:dictionaryItemId/pair/:variantId/:quantity",
    feature: "catalogue",
  },
  // One dictionary item's mappings across every back-end (catalogue
  // third-party fulfilment brief §8, journey D; Ben, 24 Sep) — one page, a
  // column per back-end, so a later supplier adapter is another column.
  dictionaryItemMappings: {
    path: "settings/catalogue/dictionary/:dictionaryItemId/mappings",
    feature: "catalogue",
  },

  // Live costing (product-owner decision, 12 Sep): Manufacturing renamed and
  // nested here because it exists only as a by-product of catalogue live
  // pricing. `settingsLiveCosting` itself redirects to its Status tab; the
  // five in-page tabs are separate routes so each is deep-linkable and
  // direct-load safe.
  settingsLiveCosting: {
    path: "settings/catalogue/live-costing",
    feature: "catalogue",
  },
  settingsLiveCostingStatus: {
    path: "settings/catalogue/live-costing/status",
    feature: "catalogue",
  },
  // The check ledger, in four roll-up views (design decision, 12 Sep,
  // 03_manufacturing_surfaces_design.md "setup visibility and costing
  // validation are separate"): by item, by cost line, by cost centre (the
  // never-checked routing rule's own data) and by product type.
  settingsLiveCostingChecks: {
    path: "settings/catalogue/live-costing/checks",
    feature: "catalogue",
  },
  // The cost-line drill-in, evolved from a side sheet into a full page
  // (review decision, 02_glossary_and_live_costing.md) — a cost line's own
  // identity, health, money summary, affected items and checks. Static
  // "cost-lines" segment ranks above dictionaryItemAdmin's `:dictionaryItemId`.
  settingsLiveCostingCostLine: {
    path: "settings/catalogue/live-costing/checks/cost-lines/:costLineId",
    feature: "catalogue",
  },
  settingsLiveCostingRoutingPolicy: {
    path: "settings/catalogue/live-costing/routing-policy",
    feature: "catalogue",
  },
  // The read-only mirror of Tharstern's manufacturing configuration. Access
  // itself is admin/manager/engineer (product brief §3), enforced by the
  // manufacturingSetupTier route guard.
  settingsLiveCostingManufacturingSetup: {
    path: "settings/catalogue/live-costing/manufacturing-setup",
    feature: "catalogue",
  },
  settingsLiveCostingCostCentreDetail: {
    path: "settings/catalogue/live-costing/manufacturing-setup/cost-centres/:costCentreId",
    feature: "catalogue",
  },
  settingsLiveCostingInkProfileDetail: {
    path: "settings/catalogue/live-costing/manufacturing-setup/ink-profiles/:inkProfileId",
    feature: "catalogue",
  },
  settingsLiveCostingProductTypeDetail: {
    path: "settings/catalogue/live-costing/manufacturing-setup/product-types/:productTypeId",
    feature: "catalogue",
  },
  settingsLiveCostingMaterialDetail: {
    path: "settings/catalogue/live-costing/manufacturing-setup/materials/:code",
    feature: "catalogue",
  },
  // Check settings (04_handover_2026-09-12.md): the admin parameters behind
  // Matches / Small differences / Mismatch, the same-direction repeat rule,
  // the check window and the weekly check budget. Split out of the Live
  // costing status page into its own tab, 12 Sep.
  settingsLiveCostingCheckSettings: {
    path: "settings/catalogue/live-costing/check-settings",
    feature: "catalogue",
  },

  // ── Legacy redirect sources only (no page renders at these paths) ──────
  // Each kept purely so `gateShellRoutes` has a manifest entry to gate on
  // for the `<Navigate replace>` wired in app/routes.tsx.
  catalogueDictionaryAdminLegacy: {
    path: "catalogue/dictionary",
    feature: "catalogue",
  },
  catalogueDictionaryRollingValidationLegacy: {
    path: "catalogue/dictionary/rolling-validation",
    feature: "catalogue",
  },
  catalogueDictionaryCostLineLegacy: {
    path: "catalogue/dictionary/cost-lines/:costLineId",
    feature: "catalogue",
  },
  catalogueDictionaryItemAdminLegacy: {
    path: "catalogue/dictionary/:dictionaryItemId",
    feature: "catalogue",
  },
  catalogueDictionaryCostPairLegacy: {
    path: "catalogue/dictionary/:dictionaryItemId/pair/:variantId/:quantity",
    feature: "catalogue",
  },
  settingsManufacturingSetupLegacy: {
    path: "settings/manufacturing/setup",
    feature: "catalogue",
  },
  settingsManufacturingCostCentreDetailLegacy: {
    path: "settings/manufacturing/cost-centres/:costCentreId",
    feature: "catalogue",
  },
  settingsManufacturingInkProfileDetailLegacy: {
    path: "settings/manufacturing/ink-profiles/:inkProfileId",
    feature: "catalogue",
  },
  settingsManufacturingProductTypeDetailLegacy: {
    path: "settings/manufacturing/product-types/:productTypeId",
    feature: "catalogue",
  },
  settingsManufacturingMaterialDetailLegacy: {
    path: "settings/manufacturing/materials/:code",
    feature: "catalogue",
  },
  settingsManufacturingRoutingPolicyLegacy: {
    path: "settings/manufacturing/routing-policy",
    feature: "catalogue",
  },
  settingsManufacturingLiveCostingLegacy: {
    path: "settings/manufacturing/live-costing",
    feature: "catalogue",
  },
  // Jobs list and detail are READ surfaces over live job data — what sales
  // asked production for — so they are live while tracking, the operational
  // write, is not. Same read-in / write-out cut as estimates.
  jobs: {
    path: "jobs",
    feature: "jobs_view",
  },
  // Redirect sources only (no page renders here): the old matching workbench, its All purchase invoices index
  // and a purchase invoice's own page. Xero bills carry `/purchase-invoices/{id}` (the push's `bill_url`), so these
  // keep resolving, to One's purchase invoice list and record (PIM-3b). `all` is static, so the router ranks it
  // above `:invoiceId`; a purchase invoice id is never the word "all".
  purchaseInvoices: {
    path: "purchase-invoices",
    feature: "purchase_invoice_matching",
  },
  purchaseInvoiceIndex: {
    path: "purchase-invoices/all",
    feature: "purchase_invoice_matching",
  },
  purchaseInvoice: {
    path: "purchase-invoices/:invoiceId",
    feature: "purchase_invoice_matching",
  },
  // The retired variance authorisation page (PIM-6): its old links, by request id, redirect
  // to the ticket the request raised (subject `variance_authorisation`), where the buyer answers.
  varianceAuthorisation: {
    path: "variance-authorisations/:requestId",
    feature: "purchase_invoice_matching",
  },
  // The PO side of the same ledger: open orders with no invoice against them
  // yet, bucketed by receipt. Same feature as the invoice queue — one surface.
  purchaseOrders: {
    path: "purchase-orders",
    feature: "purchase_invoice_matching",
  },
  // The supplier object page — one supplier concept (sup_supplier), keyed on
  // its CIS number (SUP-H40217); the Tharstern code is only a cross-reference.
  // The purchase order page's breadcrumb lands here.
  suppliers: {
    path: "suppliers",
    feature: "purchase_invoice_matching",
  },
  supplier: {
    path: "suppliers/:supplierNo",
    feature: "purchase_invoice_matching",
  },
  // The supplier page's tabs beyond Now (SUPPLIER_TABS).
  supplierTab: {
    path: "suppliers/:supplierNo/:tab",
    feature: "purchase_invoice_matching",
  },
  // The sales ledger's document pages (doc 50 §9 item 3): an invoice, a
  // pro-forma and a credit note, by their CIS numbers.
  invoiceDetail: {
    path: "invoices/:invoiceNo",
    feature: "sales_ledger",
  },
  proformaDetail: {
    path: "proformas/:proformaNo",
    feature: "sales_ledger",
  },
  creditNoteDetail: {
    path: "credit-notes/:creditNoteNo",
    feature: "sales_ledger",
  },
  // Awaiting payment (docs/features/order-to-invoice/
  // 50_finance_sales_ledger_module_spec.md §9 item 5): the pro-formas still
  // asking for money, each with Record payment for finance.
  financeAwaitingPayment: {
    path: "finance/awaiting-payment",
    feature: "sales_ledger",
  },
  // Aged debt and disputes (docs/features/v1-completion/L_credit_control_spec.md §4.7): the payers that owe on CIS
  // credit-account invoices by days overdue, and the disputes finance has recorded on them.
  financeAgedDebt: {
    path: "finance/aged-debt",
    feature: "sales_ledger",
  },
  financeAgedDebtTab: {
    path: "finance/aged-debt/:tab",
    feature: "sales_ledger",
  },
  // Ledger rules: how a purchase invoice line is coded and whether it is spread over a period, applied
  // at capture and stored in the purchase ledger, so gated with that ledger's
  // own rules routes (spec 05 §9). Last in the Finance menu (Ben, 3 Oct).
  financeLedgerRules: {
    path: "finance/rules",
    feature: "purchase_invoice_matching",
  },
  financeLedgerRulesTab: {
    path: "finance/rules/:tab",
    feature: "purchase_invoice_matching",
  },
  // Prepress home (docs/features/prepress-orchestration/
  // 17_safe_to_print_gate.md §"The prepress surface") — the one prepress
  // dashboard: safe-to-print gate, blocked (failed/parked runs + stray
  // tickets), artwork triage, and an ambient in-flight list. Consolidates
  // the former separate prepressIntakeOrphans and prepressProblemQueue
  // routes onto this single entry. Same production_ops gate.
  prepressHome: {
    path: "prepress/home",
    feature: "production_ops",
  },
  // Merged job workspace (docs/features/estimate-job-transformation/) — one
  // job's whole lifecycle (production, delivery, commercial, activity) on
  // one page, keyed on the Tharstern job number. Took over `jobs/:jobNo`
  // from the retired JobDetailPage once the merge was approved.
  jobWorkspace: {
    path: "jobs/:jobNo",
    feature: "jobs_view",
  },
  // Orders (CIS): the tracked object on order to invoice's track, beside
  // Tharstern's Jobs (docs/features/order-to-invoice/08_order_pages_frontend_spec.md).
  // Staff only, behind the `orders` feature.
  orders: {
    path: "orders",
    feature: "orders",
  },
  orderDetail: {
    path: "orders/:orderNo",
    feature: "orders",
  },
  // The order page's old tab addresses: each redirects to the state that
  // replaced it (ORDER_TAB_REDIRECTS). The order page itself has no tabs.
  orderTab: {
    path: "orders/:orderNo/:tab",
    feature: "orders",
  },
  // Quotes (CIS, `QT-` numbers): built as draft quotes, sent and live
  // until the customer decides (docs/features/order-to-invoice/
  // 40_quotes_module_spec.md). Beside Estimates, which is Tharstern's only.
  // Staff only, behind the `quotes` feature.
  quotes: {
    path: "quotes",
    feature: "quotes",
  },
  quoteDetail: {
    path: "quotes/:quoteNo",
    feature: "quotes",
  },
  // A purchase order by its number (PO-K20417): its lines, each for an order
  // line or a stock item. CSRs see the PO line inline on the order.
  purchaseOrderDetail: {
    path: "purchase-orders/:purchaseOrderNo",
    feature: "orders",
  },
  // The purchase order page's tabs beyond Now (PURCHASE_ORDER_TABS).
  purchaseOrderTab: {
    path: "purchase-orders/:purchaseOrderNo/:tab",
    feature: "orders",
  },
  // Needs attention (v1 completion I §10): the alerts on orders, their lines
  // and purchase orders a person should act on; an alert opens in a side sheet.
  needsAttention: {
    path: "needs-attention",
    feature: "orders",
  },
  // Settings › Needs attention rules: every rule, Edit rule… for an admin.
  settingsNeedsAttentionRules: {
    path: "settings/needs-attention-rules",
    feature: "orders",
  },
  // Production › Purchasing (order to invoice doc 57 §3): purchase orders to
  // place, despatch due, arriving, all; beside Orders under Production.
  purchasing: {
    path: "production/purchasing",
    feature: "orders",
  },
  // Placing one purchase order by hand (doc 57 §4): a placement is one PO.
  placement: {
    path: "production/purchasing/place/:purchaseOrderNo",
    feature: "orders",
  },
  // Sales > Promo Office — read-only view of Promo Office's own open
  // Tenders (quotes) and Indents (jobs). See
  // docs/features/promo-office-visibility/01_product_brief.md.
  promoOffice: {
    path: "promo-office",
    feature: "promo_office_visibility",
  },
  contacts: {
    path: "contacts",
    feature: "customers",
  },
  contactDetail: {
    path: "contacts/:id",
    feature: "customers",
  },
  issues: {
    path: "issues",
    feature: "tickets",
  },
  raiseIssue: {
    path: "issues/new",
    feature: "tickets",
  },
  issueDetail: {
    path: "issues/:id",
    feature: "tickets",
  },
  targets: {
    path: "targets",
    feature: "planning",
  },
  targetPlanning: {
    path: "target-planning",
    feature: "planning",
  },
  // Forecast / Targets redesign — four persona-aligned workspaces.
  plan: {
    path: "plan",
    feature: "planning",
  },
  planRep: {
    path: "plan/:repId",
    feature: "planning",
  },
  planBucket: {
    path: "plan/:repId/:bucketId",
    feature: "planning",
  },
  planTargets: {
    path: "plan/targets",
    feature: "planning",
  },
  // The rep's weekly maintenance pass over their own book (month-landing B2).
  myPipeline: {
    path: "plan/my-pipeline",
    feature: "planning",
  },
  // Settling new work against the projection that predicted it.
  myPipelineArrivals: {
    path: "plan/my-pipeline/arrivals",
    feature: "planning",
  },
  // A manager reading one of their reports' books. The same page and the same
  // levers — the server decides who may be asked for (self, anyone in your
  // reporting subtree, or anyone if you are an admin), so this route carries no authority of its
  // own. Declared after `arrivals` for readability; React Router ranks the
  // static segment above this dynamic one regardless.
  repPipeline: {
    path: "plan/my-pipeline/:repId",
    feature: "planning",
  },
  // A report's arrivals. Without this the workspace's "Settle them" would
  // carry a manager from a rep's book to their own queue — the same page
  // showing a different person's work, which is worse than no link.
  repPipelineArrivals: {
    path: "plan/my-pipeline/:repId/arrivals",
    feature: "planning",
  },
  // Every lever pull, with its before, after and reason.
  interventionLog: {
    path: "plan/interventions",
    feature: "planning",
  },
  // The manager's decomposition of why the forecast moved.
  forecastMovement: {
    path: "plan/movement",
    feature: "planning",
  },
  planCycleReviewIndex: {
    path: "plan/cycles/review",
    feature: "planning",
  },
  planCycleReview: {
    path: "plan/cycles/:cycleId/review",
    feature: "planning",
  },
  featureSessions: {
    path: "feature-sessions",
    feature: "dev_tooling",
  },
  featureSessionWorkspace: {
    path: "feature-sessions/:branchName",
    feature: "dev_tooling",
  },
  insights: {
    path: "insights",
    feature: "insights",
  },
  reports: {
    path: "reports",
    feature: "insights",
  },
  // Company + per-rep contribution/revenue/target figures, one table
  // (sales-dashboard-hub, product brief §5 point 4).
  repFigures: {
    path: "rep-figures",
    feature: "insights",
  },
  // Accounts' month-end accrual/deferral upload. Under the Insights area
  // because the Month Landing tab is where its effect is read; reached from
  // that tab rather than the global nav, and gated on FORECASTING_ACCRUALS.
  insightsAccruals: {
    path: "insights/accruals",
    feature: "insights",
  },

  // Outbound Marketing & Sales Engine — workspace with inner tabs
  // (Calendar / Campaigns / Audiences / Consent / Landing pages / Results).
  marketingCalendar: {
    path: "marketing/calendar",
    feature: "marketing",
  },
  marketingCampaigns: {
    path: "marketing/campaigns",
    feature: "marketing",
  },
  marketingCampaignWorkspace: {
    path: "marketing/campaigns/:campaignId",
    feature: "marketing",
  },
  marketingAudiences: {
    path: "marketing/audiences",
    feature: "marketing",
  },
  marketingAudienceBuilder: {
    path: "marketing/audiences/:audienceId",
    feature: "marketing",
  },
  marketingConsent: {
    path: "marketing/consent",
    feature: "marketing",
  },
  marketingResults: {
    path: "marketing/results",
    feature: "marketing",
  },
  marketingLandingPages: {
    path: "marketing/landing-pages",
    feature: "marketing",
  },
  marketingTriggers: {
    path: "marketing/triggers",
    feature: "marketing",
  },
  marketingTrigger: {
    path: "marketing/triggers/:triggerId",
    feature: "marketing",
  },
  marketingLandingPageSubmissions: {
    path: "marketing/landing-pages/:leadSourceId/leads",
    feature: "marketing",
  },
  marketingPeople: {
    path: "marketing/people",
    feature: "marketing",
  },
  marketingPersonDetail: {
    path: "marketing/people/:personId",
    feature: "marketing",
  },

  // Persona-specific home dashboards (sales vs CSR/ops). The toggle in
  // the account menu picks the default; `/` redirects per role + the
  // last toggled preference (per-device localStorage).
  homeSales: {
    path: "home/sales",
    feature: "sales_home",
  },
  homeCsr: {
    path: "home/csr",
    feature: "csr_home",
  },
  // Alternative CSR home layout — under side-by-side evaluation against
  // `homeCsr` (see CsrHomeAlt.tsx). Reached by URL only: deliberately not
  // wired into nav or the persona toggle.
  homeCsrAlt: {
    path: "home/csr/alt",
    feature: "csr_home",
  },
  homeOps: {
    path: "home/ops",
    feature: "production_ops",
  },
  // The finance team's home: the purchase ledger at a glance. Gated with the
  // purchase-invoice feature because that ledger is its whole content today.
  homeFinance: {
    path: "home/finance",
    feature: "purchase_invoice_matching",
  },

  // Work-visibility composed views (CSR-facing).
  tracking: {
    path: "tracking",
    feature: "production_ops",
  },
  // CIS-owned stock control (stock_module), a Production leaf.
  stock: {
    path: "stock",
    feature: "stock_control",
  },
  stockItemDetail: {
    path: "stock/:stockItemId",
    feature: "stock_control",
  },
  // The Stock page's "Replenishment" tab: advise proposals (BrandHub doc 09).
  stockReplenishment: {
    path: "stock/replenishment",
    feature: "stock_replenishment",
  },
  stockReplenishmentProposal: {
    path: "stock/replenishment/:proposalId",
    feature: "stock_replenishment",
  },
  // Manager/admin surface for CSR account allocation — the reallocation
  // queue + claims tabs (docs/features/csr-account-allocation/). Nav
  // visibility is additionally role-gated (manager/admin) in
  // DashboardLayout — the feature flag alone only controls environment
  // rollout, not who should see the entry.
  accountAllocation: {
    path: "account-allocation",
    feature: "account_allocation",
  },
  // Settings area — single configuration surface, with admin-only sub-pages
  settingsHome: {
    path: "settings",
    feature: "settings_home",
  },
  settingsIntegrations: {
    path: "settings/integrations",
    feature: "admin_ops",
  },
  settingsSignals: {
    path: "settings/signals",
    feature: "recommendations_config",
  },
  settingsSignalsCreate: {
    path: "settings/signals/create",
    feature: "recommendations_config_edit",
  },
  settingsSignalEdit: {
    path: "settings/signals/:id/edit",
    feature: "recommendations_config_edit",
  },
  settingsAlerts: {
    path: "settings/alerts",
    feature: "recommendations_config",
  },
  settingsAlertsCreate: {
    path: "settings/alerts/create",
    feature: "recommendations_config_edit",
  },
  settingsAlertEdit: {
    path: "settings/alerts/:id/edit",
    feature: "recommendations_config_edit",
  },
  settingsRecommendations: {
    path: "settings/recommendations",
    feature: "recommendations_config",
  },
  settingsRecommendationsCreate: {
    path: "settings/recommendations/create",
    feature: "recommendations_config_edit",
  },
  settingsRecommendationEdit: {
    path: "settings/recommendations/:id/edit",
    feature: "recommendations_config_edit",
  },

  settingsScenarios: {
    path: "settings/scenarios",
    feature: "recommendations_config_edit",
  },

  settingsBulkDefinitions: {
    path: "settings/bulk-definitions",
    feature: "recommendations_config_edit",
  },

  settingsCatalogues: {
    path: "settings/catalogues",
    feature: "catalogue",
  },
  settingsCatalogueEdit: {
    path: "settings/catalogues/:catalogueId",
    feature: "catalogue",
  },
  settingsPricing: {
    path: "settings/pricing",
    feature: "catalogue",
  },
  settingsPricingEdit: {
    path: "settings/pricing/:groupId",
    feature: "catalogue",
  },
  settingsAssignments: {
    path: "settings/assignments",
    feature: "admin_access",
  },

  settingsJobs: {
    path: "settings/jobs",
    feature: "admin_ops",
  },

  settingsUsers: {
    path: "settings/users",
    feature: "admin_access",
  },

  settingsFeedback: {
    path: "settings/feedback",
    feature: "feedback",
  },

  settingsFeedbackDetail: {
    path: "settings/feedback/:feedbackId",
    feature: "feedback",
  },

  settingsPatternDiscovery: {
    path: "settings/pattern-discovery",
    feature: "recommendations_config",
  },

  // Performance wallboard token management — admin-only surface
  // for rotating the single shared capability token. Access via
  // Permit.WALLBOARD_MANAGE. See
  // docs/features/performance-wallboard/03_wallboard_module_spec.md §9.
  settingsWallboard: {
    path: "settings/wallboard",
    feature: "shopfloor",
  },

  // Shop-floor kiosk terminal management — admin-only surface for
  // provisioning/re-siting/rotating/deactivating kiosk terminals. Access
  // via Permit.SHOPFLOOR_KIOSK_MANAGE. See
  // docs/features/shopfloor-barcode-scanning/03_module_spec_shopfloor_kiosk.md §9.
  settingsShopfloorKiosk: {
    path: "settings/shopfloor-kiosk",
    feature: "shopfloor",
  },

  // Feature state — read-only admin/engineer view of
  // modules/features.yaml as this environment resolves it.
  // Permit.FEATURES_READ. There is no edit surface: features change
  // through pull requests. See
  // docs/features/staging-environment/03_feature_mechanism_spec.md.
  settingsFeatures: {
    path: "settings/features",
    feature: "admin_ops",
  },

  // Ticket category taxonomy admin — Permit.WORKFLOW_MANAGE_CATEGORIES.
  settingsTicketCategories: {
    path: "settings/ticket-categories",
    feature: "tickets_config",
  },
  // Buddy-list admin (csr-ticket-routing) — manager/admin only. The data
  // concept and this admin page are both role-agnostic (see BuddyListEntry
  // in data/adapter.ts); only today's routing consumer (CSR ticket
  // absence-cover) and thus the admin picker's candidate roster are
  // CSR-scoped for now.
  settingsBuddies: {
    path: "settings/buddies",
    feature: "tickets_config",
  },
  // Department admin — Permit.USERS_MANAGE_DEPARTMENTS (admin/manager).
  // Same feature umbrella as the two sibling ticket-system settings pages
  // above (ticket categories, buddies) — see useCanManageDepartments.
  settingsDepartments: {
    path: "settings/departments",
    feature: "tickets_config",
  },

  // Setup › Portal sign-in: the defaults every customer and supplier starts from. Admin only
  // (Permit.PORTAL_SIGN_IN_POLICY, docs/features/portal-sign-in/05). Bespoke Setup page, 09 section 5.9.
  settingsPortalSignIn: {
    path: "settings/portal-sign-in",
    feature: "admin_ops",
  },

  // Email-capture sender/domain denylist admin — Permit.
  // CUSTOMER_COMMUNICATIONS_MANAGE_EXCLUSIONS (admin/manager). Changes what
  // the system records for everyone, so it is gated on its own dedicated
  // permit rather than the general comms read/write ones — see
  // useCanManageEmailCaptureExclusions.
  settingsEmailCaptureExclusions: {
    path: "settings/email-capture-exclusions",
    feature: "communications",
  },
  // Settings > Catalogue migration (v1 completion F; was Stock cutover, BrandHub doc 10): admin only, and temporary;
  // it goes with stock_cutover_tharstern_module once the last owner cuts over.
  settingsCatalogueMigration: {
    path: "settings/catalogue-migration",
    feature: "stock_cutover",
  },
  settingsCatalogueMigrationCustomer: {
    path: "settings/catalogue-migration/:customerId",
    feature: "stock_cutover",
  },

  // Customer-facing quote print/preview — sell pricing only, no cost/margin.
  // Outside the dashboard shell for a clean print layout, but still requires
  // normal sign-in (not in PUBLIC_ROUTE_PATHS below) — this is a staff-facing
  // preview step, not yet a capability-URL sent directly to a customer.
  promoOfficeQuotePrint: {
    path: "/promo-office/quotes/:tenderId/print",
    feature: "promo_office_visibility",
  },

  // Public preview (top-level, outside the dashboard shell)
  sharedPreview: {
    path: "/previews/:shareToken",
    feature: "dev_tooling",
  },
  // Performance wallboard (top-level, outside the dashboard shell).
  // Capability-URL gated — the `key` query param IS the credential.
  // No Entra auth, no nav chrome, no interaction. Designed for
  // unattended on-site screens. See
  // docs/features/performance-wallboard/01_product_brief.md.
  wallboard: {
    path: "/wallboard",
    feature: "shopfloor",
  },

  // Exeter site production board (top-level, outside the dashboard shell).
  // Capability-URL gated — the `key` query param IS the credential, scoped
  // to this one site. No Entra auth, no nav chrome, no interaction.
  // Designed for an unattended shop-floor TV. See
  // docs/features/site-production-board/01_product_brief.md.
  exeterBoard: {
    path: "/exeter",
    feature: "shopfloor",
  },

  // Shop-floor kiosk terminal (top-level, outside the dashboard shell).
  // Capability-token gated, but — unlike wallboard/exeterBoard — the token
  // never rides in this URL. It's provisioned once out-of-band per
  // physical terminal and persisted in this browser (see useKioskAuth),
  // sent as an `X-Kiosk-Terminal-Token` header on every kiosk API call.
  // No Entra auth, no nav chrome. Interactive (badge/PIN login, scanning),
  // unlike the read-only wallboard/site-board screens. See
  // docs/features/shopfloor-barcode-scanning/01_product_brief.md.
  shopfloorKiosk: {
    path: "/shopfloor-kiosk",
    feature: "shopfloor",
  },

  // Customer portal — its own app now (`apps/customer-portal`, per brief
  // §14), on its own hostname (`orders.wearepollards.com`, per
  // `04_api_customer_boundary_spec.md` §9), with its own auth model
  // (customers, not staff). These entries stay in this shared manifest
  // because the portal app consumes `routes.portalOrders` /
  // `paths.portalOrders()` etc. the same way the staff app consumes every
  // other entry here — NOT because the portal is still routed inside
  // apps/frontend (it no longer is; see apps/frontend/src/app/routes.tsx).
  // Not in PUBLIC_ROUTE_PATHS below: that list only matters to the staff
  // app's Entra gate, and these paths are never served on the staff origin.
  //
  // Paths carry no `/portal` prefix: the portal is a standalone shell that
  // owns its whole origin, so a `/portal` segment would be pure redundancy
  // (`orders.wearepollards.com/portal/orders`) rather than a namespace that
  // disambiguates from anything else served on that host. Route ids keep the
  // `portal*` prefix for readability within the shared manifest, where they
  // sit alongside every other app's routes.
  portalSignIn: {
    path: "/sign-in",
    feature: "customer_portal",
  },
  // The portal's branded home page (docs/features/portal-theme), the index route: the customer's banner, welcome,
  // what needs them and their recent orders. Where the theme read fails the index opens Orders, as before themes.
  portalHome: {
    path: "/",
    feature: "portal_theme",
  },
  // The portal staff view's Base themes area (`/portal-view/base-themes`, docs/features/portal-theme spec 07 §5): the
  // Pollards looks and shared customer brands every customer's theme starts from. Customer-less, beside the customer,
  // person and preview branches of the staff-view router (apps/customer-portal/src/staff-view/routes.tsx).
  portalViewBaseThemes: {
    path: "/base-themes",
    feature: "portal_theme",
  },
  portalViewBaseTheme: {
    path: "/base-themes/:baseThemeId",
    feature: "portal_theme",
  },
  // What the sign-in callback decided once Entra verified the email (docs/
  // features/customer-storefront/12_decision_access_for_every_customer.md).
  // Outside PortalLayout like sign-in itself: the person has no customer yet.
  portalWelcome: {
    path: "/welcome",
    feature: "customer_portal",
  },
  portalChooseCustomer: {
    path: "/choose-customer",
    feature: "customer_portal",
  },
  portalNoAccess: {
    path: "/no-access",
    feature: "customer_portal",
  },
  portalCustomerBlocked: {
    path: "/customer-blocked",
    feature: "customer_portal",
  },
  portalOrders: {
    path: "/orders",
    feature: "customer_portal",
  },
  // One address, two pages: a Tharstern job's order ref opens the order detail page, a CIS order's number
  // (`ORD-K48193`, domain/cis-order-no.ts) opens the CIS order page. A CIS order is not under its own prefix
  // because the card checkout returns the customer to `/orders/{order_no}` (orders_module's signed-in pay),
  // and one URL per order is better than two. The route's page picks by the number's shape.
  portalOrderDetail: {
    path: "/orders/:orderId",
    feature: "customer_portal",
  },
  // A multi-line sales order's own page (the Orders list's row links here
  // when line_count > 1; a one-line order or an orderless job goes
  // straight to portalOrderDetail instead — see PortalOrdersPage.tsx).
  portalOrderLines: {
    path: "/orders/:orderNo/lines",
    feature: "customer_portal",
  },
  // The customer's CIS quotes (P4f): the list, shown when the session says there is at least one, and one
  // quote's signed-in page (Ben's read, `GET /views/customer/quote-page/{quote_no}`).
  portalQuotes: {
    path: "/quotes",
    feature: "customer_portal",
  },
  portalQuoteDetail: {
    path: "/quotes/:quoteNo",
    feature: "customer_portal",
  },
  portalSpend: {
    path: "/spend",
    feature: "customer_portal",
  },
  // Artwork preflight check journey — wired to artwork_intake_module's
  // customer_artwork_check_router via CustomerDataAdapter. Split into an
  // upload page and a result page.
  portalArtworkCheck: {
    path: "/artwork-check",
    feature: "customer_portal",
  },
  portalArtworkCheckResult: {
    path: "/artwork-check/:checkId",
    feature: "customer_portal",
  },

  // The customer's own catalogue (docs/features/customer-portal-ordering) —
  // browse, configure and price. Placing and reordering return on the basket
  // and orders (order to invoice step 3b).
  portalCatalogue: {
    path: "/catalogue",
    feature: "customer_portal",
  },
  portalCatalogueItem: {
    path: "/catalogue/:itemId",
    feature: "customer_portal",
  },
  // The customer's basket, its checkout and the order-placed page (docs/features/customer-portal-ordering/17).
  // The placed page is also where the card payment page returns (`?payment=return` or `?payment=cancelled`).
  portalBasket: {
    path: "/basket",
    feature: "customer_portal",
  },
  portalCheckout: {
    path: "/basket/checkout",
    feature: "customer_portal",
  },
  portalOrderPlaced: {
    path: "/basket/placed/:orderNo",
    feature: "customer_portal",
  },
  // The customer's quote and order pages by their emailed link (order to
  // invoice doc 40 §10, doc 47 §5; Ben and Brad, 5 Oct): no sign-in, the
  // token in the path is the whole credential, as the backend's public token
  // routes treat it. Outside PortalLayout (whose session fetch would bounce a
  // link holder to /sign-in).
  portalQuoteLink: {
    path: "/quote/:token",
    feature: "customer_portal",
  },
  portalOrderLink: {
    path: "/order/:token",
    feature: "customer_portal",
  },
  // One proof by its own emailed link (doc 66 §6.4): the same credential, for one proof and nothing else.
  portalProofLink: {
    path: "/proof/:token",
    feature: "customer_portal",
  },

  // Staff entry to portal access: jump to any customer's portal people, plus
  // the cross-customer view (recent sign-ins, blocked customers, access
  // awaiting approval). A top-level page rather than a Settings tab: staff
  // reach for it from a customer they are already thinking about, and the
  // per-customer management itself lives in the portal's staff view
  // (/portal-view/customer/:id/people), beside "view as" (docs/features/
  // customer-storefront/12_decision_access_for_every_customer.md).
  customerPortalAccess: {
    path: "customer-portal",
    feature: "customer_portal",
  },

  // Supplier portal — its own app (`apps/supplier-portal`) on its own
  // hostname, so paths carry no prefix (docs/features/supplier-interface/).
  // One set of pages, two ways in: the purchase order page is the same route
  // by emailed link (its token in the URL #fragment, so it never reaches a
  // server or a Referer) and by login. The list, invoices and account need a
  // login.
  supplierPurchaseOrder: {
    path: "/po/:purchaseOrderNo",
    feature: "supplier_portal",
  },
  supplierOrders: {
    path: "/orders",
    feature: "supplier_portal",
  },
  // A supplier's quote request (spec 33): like the purchase order, one page by emailed link or by login; the
  // list needs a login.
  supplierQuoteRequest: {
    path: "/quote-requests/:supplierQuoteNo",
    feature: "supplier_portal",
  },
  supplierQuoteRequests: {
    path: "/quote-requests",
    feature: "supplier_portal",
  },
  supplierInvoices: {
    path: "/invoices",
    feature: "supplier_portal",
  },
  supplierAccount: {
    path: "/account",
    feature: "supplier_portal",
  },
  // Your company (supplier diligence, docs/features/supplier-diligence/04): logins only. The open assessment, the
  // certificates and the past assessments; the assessment form itself opens a section with `?section=`.
  supplierCompany: {
    path: "/company",
    feature: "supplier_portal",
  },
  supplierAssessment: {
    path: "/company/assessment",
    feature: "supplier_portal",
  },
  // Warehouse picks (supplier interface spec 14 §8.2): logins only, shown when
  // the supplier runs a warehouse for us. `?warehouse=&tab=` on the list.
  supplierPicks: {
    path: "/picks",
    feature: "supplier_portal",
  },
  supplierPick: {
    path: "/picks/:pickId",
    feature: "supplier_portal",
  },
  // Goods in at a warehouse the supplier operates (spec 22 §6): logins only. `?warehouse=&tab=` on the list.
  supplierGoodsIn: {
    path: "/goods-in",
    feature: "supplier_portal",
  },
  supplierSignIn: {
    path: "/sign-in",
    feature: "supplier_portal",
  },
} as const satisfies Record<string, RouteDefinition>;

/**
 * A cost line's id is `${costCentreId}:${component}` (mock-data/
 * cost-engine-validation.ts's own key), URL-encoded as a single route
 * segment. Shared codec so `settingsLiveCostingCostLine` and its page
 * component never duplicate the scheme.
 */
/** A fixed-cost corner's switched-on options as sorted "FieldKey:code"
 *  values — the one URL spelling, shared by the pair page's route and the
 *  cost-pair API query (`fixed_cost_option`). */
export function fixedCostOptionSearchValues(
  options: readonly { fieldKey: string; optionCode: string }[],
): string[] {
  return options.map((o) => `${o.fieldKey}:${o.optionCode}`).sort();
}

export function encodeCostLineId(p: {
  costCentreId: number;
  component: string;
}): string {
  return encodeURIComponent(`${p.costCentreId}:${p.component}`);
}

export function decodeCostLineId(
  costLineId: string,
): { costCentreId: number; component: string } | null {
  const decoded = decodeURIComponent(costLineId);
  const separatorIndex = decoded.indexOf(":");
  if (separatorIndex === -1) return null;
  const costCentreId = Number(decoded.slice(0, separatorIndex));
  const component = decoded.slice(separatorIndex + 1);
  if (!Number.isFinite(costCentreId) || !component) return null;
  return { costCentreId, component };
}

/**
 * The estimate page's tabs, in order; Now is the default and has no suffix
 * (docs/features/journey-visual-language/01_proposal.md §4). Details sits
 * second (Ben, 2 Oct): the estimate's reference facts, before its lines.
 */
export const ESTIMATE_TABS = [
  "now",
  "details",
  "lines",
  "price",
  "delivery",
  "billing",
  "activity",
] as const;
export type EstimateTab = (typeof ESTIMATE_TABS)[number];

/**
 * Tab addresses the estimate page used to have, and where each lives now —
 * so a link that went out still lands (Payment became part of Billing; the
 * Artwork tab retired with the catalogue estimate link, order to invoice
 * step 3b).
 */
export const RETIRED_ESTIMATE_TABS: Readonly<Record<string, EstimateTab>> = {
  payment: "billing",
  artwork: "now",
};

/** The customer frame's built tabs, in order; Overview is the default. */
export const CUSTOMER_TABS = [
  "overview",
  "catalogue",
  "estimates",
  "quotes",
  "orders",
  "jobs",
  "settings",
  "invoices",
] as const;
export type CustomerTab = (typeof CUSTOMER_TABS)[number];

/**
 * The order page's view, all in its query (docs/features/order-to-invoice/
 * 28_order_page_one_card_implementation_summary.md): the chain step open
 * under the rail, at a line and one of its versions; Details; the Activity
 * sheet; the action box opened. A deep link and Back land on any of them.
 */
export interface OrderPageView {
  /** The rail's open step. */
  step?: OrderRailStep;
  /** The step opened at a line (".01"), and at one of its versions. */
  line?: string;
  version?: string;
  /** Details open under the headline. */
  details?: boolean;
  /** The Activity side sheet open. */
  activity?: boolean;
  /** The action box opened: every row. */
  now?: boolean;
}

/**
 * The order page's old tab addresses (`/orders/:orderNo/:tab`), and the
 * state each opens now that the page is one card (Ben, 3 Oct): Lines and
 * Delivery are rail steps; Now, Details and Activity are in the card.
 * Artwork and Billing were planned tabs; they open their planned steps.
 */
export const ORDER_TAB_REDIRECTS: Readonly<
  Record<string, Pick<OrderPageView, "step" | "details" | "activity" | "now">>
> = {
  now: { now: true },
  details: { details: true },
  activity: { activity: true },
  lines: { step: "order" },
  delivery: { step: "delivery" },
  artwork: { step: "artwork" },
  billing: { step: "invoice" },
};

/** The order page's view as its query: `step`, `line`, `version`, then the flags. */
export function orderViewQuery(view: OrderPageView): URLSearchParams {
  const query = new URLSearchParams();
  if (view.step) query.set("step", view.step);
  if (view.step && view.line) query.set("line", view.line);
  if (view.step && view.line && view.version)
    query.set("version", view.version);
  if (view.details) query.set("details", "1");
  if (view.activity) query.set("activity", "1");
  if (view.now) query.set("now", "1");
  return query;
}

/**
 * The quote page's view, all in its query, as the order page's: the chain
 * step open under the rail (at a line), Details, the Activity sheet, the
 * action box opened.
 */
export interface QuotePageView {
  step?: QuoteRailStep;
  /** The Published step opened at a line (".01"). */
  line?: string;
  details?: boolean;
  activity?: boolean;
  now?: boolean;
}

/** The quote page's view as its query: `step`, `line`, then the flags. */
export function quoteViewQuery(view: QuotePageView): URLSearchParams {
  const query = new URLSearchParams();
  if (view.step) query.set("step", view.step);
  if (view.step && view.line) query.set("line", view.line);
  if (view.details) query.set("details", "1");
  if (view.activity) query.set("activity", "1");
  if (view.now) query.set("now", "1");
  return query;
}

/** The purchase order page's tabs, in order; Now is the default. */
export const PURCHASE_ORDER_TABS = [
  "now",
  "details",
  "lines",
  "receipts",
  "purchase-invoices",
  "activity",
] as const;
export type PurchaseOrderTab = (typeof PURCHASE_ORDER_TABS)[number];

/**
 * Typed path builders. Each function takes a params object (where relevant)
 * and returns the full absolute URL suitable for `<Link to={...}>` or
 * `navigate(...)` in react-router, or for any future native navigator.
 */
export const paths = {
  root: (): string => "/",
  objectList: (p: { objectId: string; search?: string }): string =>
    `/objects/${encodeURIComponent(p.objectId)}${p.search ? `?${p.search}` : ""}`,
  objectRecord: (p: {
    objectId: string;
    recordKey: string;
    search?: string;
  }): string =>
    `/objects/${encodeURIComponent(p.objectId)}/${encodeURIComponent(p.recordKey)}${p.search ? `?${p.search}` : ""}`,
  find: (p: { q: string }): string =>
    `/find?${new URLSearchParams({ q: p.q.trim() }).toString()}`,
  customers: (): string => "/customers",
  customerCreate: (): string => "/customers/new",
  customerDetail: (p: { customerId: string }): string =>
    `/customers/${p.customerId}`,
  accountAllocation: (): string => "/account-allocation",
  customerOrders: (p: { customerId: string }): string =>
    `/customers/${encodeURIComponent(p.customerId)}/orders`,
  customerOrderHistory: (p: { customerId: string }): string =>
    `/customers/${encodeURIComponent(p.customerId)}/orders/history`,
  customerQuotes: (p: { customerId: string }): string =>
    `/customers/${encodeURIComponent(p.customerId)}/quotes`,
  customerSettings: (p: { customerId: string }): string =>
    `/customers/${encodeURIComponent(p.customerId)}/settings`,
  customerInvoices: (p: { customerId: string }): string =>
    `/customers/${encodeURIComponent(p.customerId)}/invoices`,
  /** `raise` opens Raise credit note; `line` (an invoice line id) ticks that line in it (doc 98 §4). */
  invoiceDetail: (p: {
    invoiceNo: string;
    raise?: boolean;
    line?: string;
  }): string =>
    `/invoices/${encodeURIComponent(p.invoiceNo)}${p.raise ? "?raise=1" : ""}${
      p.raise && p.line ? `&line=${encodeURIComponent(p.line)}` : ""
    }`,
  proformaDetail: (p: { proformaNo: string }): string =>
    `/proformas/${encodeURIComponent(p.proformaNo)}`,
  creditNoteDetail: (p: { creditNoteNo: string }): string =>
    `/credit-notes/${encodeURIComponent(p.creditNoteNo)}`,
  creditControl: (): string => "/credit-control",
  journeyTesting: (p?: { tab?: string }): string =>
    `/testing/${p?.tab ?? "scenarios"}`,
  journeyTestingHome: (): string => "/testing",
  testingPreviewOpen: (p: { environmentId: string }): string =>
    `/previews/testing/${encodeURIComponent(p.environmentId)}`,
  customerPlanned: (p: {
    customerId: string;
    section: PlannedTabKey<"customer">;
  }): string =>
    `/customers/${encodeURIComponent(p.customerId)}/planned/${p.section}`,
  tickets: (): string => "/tickets",
  ticketDetail: (p: { id: string }): string =>
    `/tickets/${encodeURIComponent(p.id)}`,
  alerts: (): string => "/alerts",
  recommendations: (): string => "/recommendations",
  notes: (): string => "/notes",
  communications: (): string => "/communications",
  prospects: (): string => "/prospects",
  leads: (): string => "/leads",
  leadDetail: (p: { leadId: string }): string =>
    `/leads/${encodeURIComponent(p.leadId)}`,
  // `customerId` scopes the list to one customer (the page renders a scope
  // chip and the server filters); `status` picks the lifecycle tab. Both are
  // builder params rather than a hand-appended query string so a caller
  // cannot mis-spell the param the list page reads.
  estimates: (p?: { customerId?: string; status?: string }): string => {
    const query = new URLSearchParams();
    if (p?.customerId) query.set("customer", p.customerId);
    if (p?.status) query.set("status", p.status);
    return `/estimates${query.size ? `?${query}` : ""}`;
  },
  /** The estimate page; Now when no tab is named. */
  estimateDetail: (p: { id: string; tab?: EstimateTab }): string =>
    `/estimates/${p.id}${p.tab && p.tab !== "now" ? `/${p.tab}` : ""}`,
  commercialExceptions: (): string => "/commercial-exceptions",
  settingsPricingTargets: (): string => "/settings/pricing-targets",
  settingsPricingReview: (): string => "/settings/pricing-review",
  createEstimate: (p?: { customerCode?: string }): string => {
    const base = "/estimates/new";
    return p?.customerCode
      ? `${base}?customer=${encodeURIComponent(p.customerCode)}`
      : base;
  },
  catalogue: (p?: {
    scope?: string;
    customerCode?: string;
    customerName?: string;
  }): string => {
    const query = new URLSearchParams();
    if (p?.scope) query.set("scope", p.scope);
    if (p?.customerCode) query.set("customer", p.customerCode);
    if (p?.customerName) query.set("customerName", p.customerName);
    return `/catalogue${query.size ? `?${query}` : ""}`;
  },
  dictionaryWorkbook: (): string => "/catalogue/import",
  catalogueItem: (p: { itemId: string }): string => `/catalogue/${p.itemId}`,
  // `family` opens the dictionary on one product family (family › product).
  dictionaryAdmin: (p?: { family?: string }): string =>
    p?.family
      ? `/settings/catalogue/dictionary?family=${encodeURIComponent(p.family)}`
      : "/settings/catalogue/dictionary",
  dictionaryGapLog: (): string => "/settings/catalogue/dictionary/gap-log",
  dictionaryItemAdmin: (p: { dictionaryItemId: string }): string =>
    `/settings/catalogue/dictionary/${encodeURIComponent(p.dictionaryItemId)}`,
  // A fixed-cost corner (spec 03 rule (d)) is the same variant with some
  // fixed-cost options on: each travels as a repeated `fixedCostOption=
  // FieldKey:code` search value, so the plain pair's URL is unchanged.
  // A supplier route's cost at the same (variant, quantity) travels as
  // `route=<code>`; the in-house pair's URL is unchanged.
  dictionaryCostPair: (p: {
    dictionaryItemId: string;
    variantId: string;
    quantity: number;
    fixedCostOptions?: readonly { fieldKey: string; optionCode: string }[];
    routeCode?: string;
  }): string => {
    const path = `/settings/catalogue/dictionary/${encodeURIComponent(p.dictionaryItemId)}/pair/${encodeURIComponent(p.variantId)}/${p.quantity}`;
    const entries: [string, string][] = fixedCostOptionSearchValues(
      p.fixedCostOptions ?? [],
    ).map((v) => ["fixedCostOption", v]);
    if (p.routeCode) entries.push(["route", p.routeCode]);
    if (entries.length === 0) return path;
    return `${path}?${new URLSearchParams(entries).toString()}`;
  },
  dictionaryItemMappings: (p: { dictionaryItemId: string }): string =>
    `/settings/catalogue/dictionary/${encodeURIComponent(p.dictionaryItemId)}/mappings`,
  settingsLiveCosting: (): string => "/settings/catalogue/live-costing",
  settingsLiveCostingStatus: (): string =>
    "/settings/catalogue/live-costing/status",
  // Deep-linkable per view (design note "By cost centre" / "By product
  // type" rolls-up) so the Manufacturing setup detail page's "see live
  // costing checks" link — and a product-type page's future equivalent —
  // land on the right slice rather than the default view.
  settingsLiveCostingChecks: (p?: {
    view?: "item" | "cost-line" | "cost-centre" | "product-type";
    costCentreId?: number;
    productTypeId?: number;
  }): string => {
    const base = "/settings/catalogue/live-costing/checks";
    const params = new URLSearchParams();
    if (p?.view) params.set("view", p.view);
    if (p?.costCentreId != null)
      params.set("costCentreId", String(p.costCentreId));
    if (p?.productTypeId != null)
      params.set("productTypeId", String(p.productTypeId));
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  },
  // A cost line's id is its `costCentreId:component` key (mock-data/
  // cost-engine-validation.ts's own key), URL-encoded as one segment.
  // `encodeCostLineId`/`decodeCostLineId` below are the shared codec so the
  // page component never duplicates this scheme.
  settingsLiveCostingCostLine: (p: {
    costCentreId: number;
    component: string;
  }): string =>
    `/settings/catalogue/live-costing/checks/cost-lines/${encodeCostLineId(p)}`,
  settingsLiveCostingRoutingPolicy: (): string =>
    "/settings/catalogue/live-costing/routing-policy",
  settingsLiveCostingManufacturingSetup: (): string =>
    "/settings/catalogue/live-costing/manufacturing-setup",
  settingsLiveCostingCostCentreDetail: (p: { costCentreId: number }): string =>
    `/settings/catalogue/live-costing/manufacturing-setup/cost-centres/${encodeURIComponent(p.costCentreId)}`,
  settingsLiveCostingInkProfileDetail: (p: { inkProfileId: number }): string =>
    `/settings/catalogue/live-costing/manufacturing-setup/ink-profiles/${encodeURIComponent(p.inkProfileId)}`,
  settingsLiveCostingProductTypeDetail: (p: {
    productTypeId: number;
  }): string =>
    `/settings/catalogue/live-costing/manufacturing-setup/product-types/${encodeURIComponent(p.productTypeId)}`,
  settingsLiveCostingMaterialDetail: (p: { code: string }): string =>
    `/settings/catalogue/live-costing/manufacturing-setup/materials/${encodeURIComponent(p.code)}`,
  settingsLiveCostingCheckSettings: (): string =>
    "/settings/catalogue/live-costing/check-settings",
  // The work portfolio is not a route — it is the jobs page in a particular
  // view, and the model says so. `customerId` scopes the list to one customer
  // and `bucket` picks the WIP / awaiting-invoice / invoiced /
  // closed-not-billed tab.
  jobs: (p?: {
    view?: string;
    customerId?: string;
    bucket?: string;
    /** Readiness-axis filter — matches `JobsFilterValues.readiness`. */
    readiness?: string;
    /** Due-date window preset — matches `JobsFilterValues.dueWindow`. */
    dueWindow?: string;
    /** "My work" — jobs for the caller's own portfolio plus anything they
     *  booked in themselves (see `work_scope_clause`). */
    mine?: boolean;
    /** The sales view's initial ordered-date window; `all` drops it. */
    range?: "7d" | "30d" | "90d" | "all";
  }): string => {
    const query = new URLSearchParams();
    if (p?.view) query.set("view", p.view);
    if (p?.customerId) query.set("customer", p.customerId);
    if (p?.bucket) query.set("bucket", p.bucket);
    if (p?.readiness) query.set("readiness", p.readiness);
    if (p?.dueWindow) query.set("due_window", p.dueWindow);
    if (p?.mine) query.set("mine", "1");
    if (p?.range) query.set("range", p.range);
    return `/jobs${query.size ? `?${query}` : ""}`;
  },
  jobWorkspace: (p: { jobNo: string }): string =>
    `/jobs/${encodeURIComponent(p.jobNo)}`,
  orders: (): string => "/orders",
  /** The order page, in a view: a chain step open (at a line), Details, Activity, the box open. */
  orderDetail: (p: { orderNo: string } & OrderPageView): string => {
    const query = orderViewQuery(p).toString();
    return `/orders/${encodeURIComponent(p.orderNo)}${query ? `?${query}` : ""}`;
  },
  quotes: (): string => "/quotes",
  /** The quote page, in a view: a chain step open (at a line), Details, Activity, the box open. */
  quoteDetail: (p: { quoteNo: string } & QuotePageView): string => {
    const query = quoteViewQuery(p).toString();
    return `/quotes/${encodeURIComponent(p.quoteNo)}${query ? `?${query}` : ""}`;
  },
  /** The purchase order page; Now when no tab is named. */
  purchaseOrderDetail: (p: {
    purchaseOrderNo: string;
    tab?: PurchaseOrderTab;
  }): string =>
    `/purchase-orders/${encodeURIComponent(p.purchaseOrderNo)}${p.tab && p.tab !== "now" ? `/${p.tab}` : ""}`,
  promoOffice: (): string => "/promo-office",
  purchaseOrders: (): string => "/purchase-orders",
  /** Needs attention: Mine unless another queue is named; an alert's id opens its sheet. */
  needsAttention: (p?: { queue?: string; alert?: string }): string => {
    const params = new URLSearchParams();
    if (p?.queue && p.queue !== "mine") params.set("queue", p.queue);
    if (p?.alert) params.set("alert", p.alert);
    const qs = params.toString();
    return qs ? `/needs-attention?${qs}` : "/needs-attention";
  },
  settingsNeedsAttentionRules: (): string => "/settings/needs-attention-rules",
  /** Production › Purchasing; To place unless another tab is named. */
  purchasing: (p?: { tab?: string }): string =>
    p?.tab && p.tab !== "to_place"
      ? `/production/purchasing?tab=${encodeURIComponent(p.tab)}`
      : "/production/purchasing",
  /**
   * The placement page for a purchase order. In the queue (`queue`) it walks
   * To place one at a time, carrying what this run placed and skipped.
   */
  placement: (p: {
    purchaseOrderNo: string;
    queue?: { done: number; skipped: string[] };
  }): string => {
    const base = `/production/purchasing/place/${encodeURIComponent(p.purchaseOrderNo)}`;
    if (!p.queue) return base;
    const params = new URLSearchParams({ queue: "1" });
    if (p.queue.done > 0) params.set("done", String(p.queue.done));
    if (p.queue.skipped.length > 0)
      params.set("skipped", p.queue.skipped.join(","));
    return `${base}?${params.toString()}`;
  },
  suppliers: (): string => "/suppliers",
  /** The supplier page; Now when no tab is named. */
  supplier: (p: { supplierNo: string; tab?: SupplierTab }): string =>
    `/suppliers/${encodeURIComponent(p.supplierNo)}${p.tab && p.tab !== "now" ? `/${p.tab}` : ""}`,
  financeAwaitingPayment: (): string => "/finance/awaiting-payment",
  financeAgedDebt: (p?: { tab?: AgedDebtTab; q?: string }): string => {
    const base =
      p?.tab && p.tab !== "aged-debt"
        ? `/finance/aged-debt/${p.tab}`
        : "/finance/aged-debt";
    return p?.q ? `${base}?q=${encodeURIComponent(p.q)}` : base;
  },
  financeLedgerRules: (p?: { tab?: LedgerRulesTab }): string =>
    p?.tab ? `/finance/rules/${p.tab}` : "/finance/rules",
  prepressHome: (): string => "/prepress/home",
  contacts: (): string => "/contacts",
  contactDetail: (p: { id: string; customerId?: string }): string =>
    `/contacts/${encodeURIComponent(p.id)}${p.customerId ? `?customer_id=${encodeURIComponent(p.customerId)}` : ""}`,
  issues: (): string => "/issues",
  raiseIssue: (p?: { customerId?: string }): string => {
    const base = "/issues/new";
    return p?.customerId
      ? `${base}?customer=${encodeURIComponent(p.customerId)}`
      : base;
  },
  issueDetail: (p: { id: string }): string => `/issues/${p.id}`,
  targets: (): string => "/targets",
  targetPlanning: (): string => "/target-planning",
  plan: (): string => "/plan",
  planRep: (p: { repId: string }): string => `/plan/${p.repId}`,
  planBucket: (p: { repId: string; bucketId: string }): string =>
    `/plan/${p.repId}/${p.bucketId}`,
  planTargets: (): string => "/plan/targets",
  myPipeline: (): string => "/plan/my-pipeline",
  myPipelineArrivals: (): string => "/plan/my-pipeline/arrivals",
  repPipeline: ({ repId }: { repId: string }): string =>
    `/plan/my-pipeline/${encodeURIComponent(repId)}`,
  repPipelineArrivals: ({ repId }: { repId: string }): string =>
    `/plan/my-pipeline/${encodeURIComponent(repId)}/arrivals`,
  interventionLog: (): string => "/plan/interventions",
  forecastMovement: (): string => "/plan/movement",
  planCycleReviewIndex: (): string => "/plan/cycles/review",
  planCycleReview: (p: { cycleId: string }): string =>
    `/plan/cycles/${p.cycleId}/review`,
  featureSessions: (): string => "/feature-sessions",
  featureSessionWorkspace: (p: { branchName: string }): string =>
    `/feature-sessions/${p.branchName.split("/").join("~")}`,
  insights: (): string => "/insights",
  reports: (): string => "/reports",
  repFigures: (): string => "/rep-figures",
  insightsAccruals: (): string => "/insights/accruals",
  carriageCosts: (): string => "/commercial/carriage",
  marketingCalendar: (): string => "/marketing/calendar",
  marketingCampaigns: (): string => "/marketing/campaigns",
  marketingCampaignWorkspace: (p: { campaignId: string }): string =>
    `/marketing/campaigns/${p.campaignId}`,
  marketingAudiences: (): string => "/marketing/audiences",
  marketingAudienceBuilder: (p: { audienceId: string }): string =>
    `/marketing/audiences/${p.audienceId}`,
  marketingConsent: (): string => "/marketing/consent",
  marketingResults: (): string => "/marketing/results",
  marketingLandingPages: (): string => "/marketing/landing-pages",
  marketingTriggers: (): string => "/marketing/triggers",
  marketingTrigger: (p: { triggerId: string }): string =>
    `/marketing/triggers/${p.triggerId}`,
  marketingLandingPageSubmissions: (p: { leadSourceId: string }): string =>
    `/marketing/landing-pages/${p.leadSourceId}/leads`,
  marketingPeople: (): string => "/marketing/people",
  marketingPersonDetail: (p: { personId: string }): string =>
    `/marketing/people/${p.personId}`,
  homeSales: (): string => "/home/sales",
  homeCsr: (): string => "/home/csr",
  homeCsrAlt: (): string => "/home/csr/alt",
  homeOps: (): string => "/home/ops",
  homeFinance: (): string => "/home/finance",
  tracking: (): string => "/tracking",
  stock: (): string => "/stock",
  stockItemDetail: (p: { stockItemId: string }): string =>
    `/stock/${encodeURIComponent(p.stockItemId)}`,
  stockReplenishment: (): string => "/stock/replenishment",
  stockReplenishmentProposal: (p: { proposalId: string }): string =>
    `/stock/replenishment/${encodeURIComponent(p.proposalId)}`,
  settingsHome: (): string => "/settings",
  settingsIntegrations: (): string => "/settings/integrations",
  settingsSignals: (): string => "/settings/signals",
  settingsSignalsCreate: (): string => "/settings/signals/create",
  settingsSignalEdit: (p: { id: string }): string =>
    `/settings/signals/${p.id}/edit`,
  settingsAlerts: (): string => "/settings/alerts",
  settingsAlertsCreate: (): string => "/settings/alerts/create",
  settingsAlertEdit: (p: { id: string }): string =>
    `/settings/alerts/${p.id}/edit`,
  settingsRecommendations: (): string => "/settings/recommendations",
  settingsRecommendationsCreate: (): string =>
    "/settings/recommendations/create",
  settingsRecommendationEdit: (p: { id: string }): string =>
    `/settings/recommendations/${p.id}/edit`,

  settingsScenarios: (): string => "/settings/scenarios",

  settingsBulkDefinitions: (): string => "/settings/bulk-definitions",

  settingsCatalogues: (): string => "/settings/catalogues",
  settingsCatalogueEdit: (p: { catalogueId: string }): string =>
    `/settings/catalogues/${encodeURIComponent(p.catalogueId)}`,
  settingsPricing: (): string => "/settings/pricing",
  settingsPricingEdit: (p: { groupId: string }): string =>
    `/settings/pricing/${encodeURIComponent(p.groupId)}`,
  settingsAssignments: (): string => "/settings/assignments",

  settingsJobs: (): string => "/settings/jobs",

  settingsUsers: (): string => "/settings/users",
  settingsPatternDiscovery: (): string => "/settings/pattern-discovery",
  settingsWallboard: (): string => "/settings/wallboard",
  settingsShopfloorKiosk: (): string => "/settings/shopfloor-kiosk",
  settingsFeatures: (): string => "/settings/features",
  settingsTicketCategories: (): string => "/settings/ticket-categories",
  settingsBuddies: (): string => "/settings/buddies",
  settingsDepartments: (): string => "/settings/departments",
  settingsPortalSignIn: (): string => "/settings/portal-sign-in",
  settingsEmailCaptureExclusions: (): string =>
    "/settings/email-capture-exclusions",
  settingsCatalogueMigration: (): string => "/settings/catalogue-migration",
  settingsCatalogueMigrationCustomer: (p: { customerId: string }): string =>
    `/settings/catalogue-migration/${encodeURIComponent(p.customerId)}`,
  settingsFeedback: (): string => "/settings/feedback",
  settingsFeedbackDetail: (p: { feedbackId: string }): string =>
    `/settings/feedback/${p.feedbackId}`,

  sharedPreview: (p: { shareToken: string }): string =>
    `/previews/${p.shareToken}`,
  wallboard: (p?: { key?: string }): string =>
    p?.key ? `/wallboard?key=${encodeURIComponent(p.key)}` : "/wallboard",
  exeterBoard: (p?: { key?: string }): string =>
    p?.key ? `/exeter?key=${encodeURIComponent(p.key)}` : "/exeter",

  promoOfficeQuotePrint: (p: { tenderId: string }): string =>
    `/promo-office/quotes/${encodeURIComponent(p.tenderId)}/print`,

  shopfloorKiosk: (): string => "/shopfloor-kiosk",

  portalSignIn: (): string => "/sign-in",
  portalHome: (): string => "/",
  /** Under the staff view's `/portal-view` basename. */
  portalViewBaseThemes: (): string => "/base-themes",
  portalViewBaseTheme: (p: { baseThemeId: string }): string =>
    `/base-themes/${encodeURIComponent(p.baseThemeId)}`,
  // `scenario` names a review fixture (mock-data/customer-portal-sign-in.ts);
  // the real sign-in callback will carry the person's own outcome instead.
  portalWelcome: (p?: { scenario?: string }): string =>
    p?.scenario
      ? `/welcome?scenario=${encodeURIComponent(p.scenario)}`
      : "/welcome",
  portalChooseCustomer: (p?: {
    scenario?: string;
    change?: boolean;
  }): string => {
    const params = new URLSearchParams();
    if (p?.scenario) params.set("scenario", p.scenario);
    if (p?.change) params.set("change", "1");
    const query = params.toString();
    return query ? `/choose-customer?${query}` : "/choose-customer";
  },
  portalNoAccess: (p?: { scenario?: string }): string =>
    p?.scenario
      ? `/no-access?scenario=${encodeURIComponent(p.scenario)}`
      : "/no-access",
  // `customer` is the id of the customer who is not available, for a person with other customers that work.
  portalCustomerBlocked: (p?: {
    scenario?: string;
    customer?: string;
  }): string => {
    const params = new URLSearchParams();
    if (p?.scenario) params.set("scenario", p.scenario);
    if (p?.customer) params.set("customer", p.customer);
    const query = params.toString();
    return query ? `/customer-blocked?${query}` : "/customer-blocked";
  },
  portalOrders: (p?: {
    tab?: string;
    q?: string;
    // Drill-through params from the spend report's breakdown rows (§9b) —
    // narrow within the caller's scope, never widen it. Mirrors
    // CustomerOrderListOptions.accountCode/productCode 1:1.
    accountCode?: string;
    productCode?: string;
  }): string => {
    const params = new URLSearchParams();
    if (p?.tab) params.set("tab", p.tab);
    if (p?.q) params.set("q", p.q);
    if (p?.accountCode) params.set("accountCode", p.accountCode);
    if (p?.productCode) params.set("productCode", p.productCode);
    const query = params.toString();
    return query ? `/orders?${query}` : "/orders";
  },
  portalOrderDetail: (p: { orderId: string }): string =>
    `/orders/${encodeURIComponent(p.orderId)}`,
  portalOrderLines: (p: { orderNo: string }): string =>
    `/orders/${encodeURIComponent(p.orderNo)}/lines`,
  // A CIS order's page: the same address as portalOrderDetail, told apart by the number's shape.
  portalCisOrder: (p: { orderNo: string }): string =>
    `/orders/${encodeURIComponent(p.orderNo)}`,
  portalQuotes: (): string => "/quotes",
  portalQuoteDetail: (p: { quoteNo: string }): string =>
    `/quotes/${encodeURIComponent(p.quoteNo)}`,
  portalSpend: (p?: { site?: string }): string =>
    p?.site ? `/spend?site=${encodeURIComponent(p.site)}` : "/spend",
  portalArtworkCheck: (): string => "/artwork-check",
  portalArtworkCheckResult: (p: { checkId: string }): string =>
    `/artwork-check/${encodeURIComponent(p.checkId)}`,
  portalCatalogue: (): string => "/catalogue",
  portalCatalogueItem: (p: { itemId: string }): string =>
    `/catalogue/${encodeURIComponent(p.itemId)}`,
  portalBasket: (): string => "/basket",
  portalCheckout: (): string => "/basket/checkout",
  portalOrderPlaced: (p: {
    orderNo: string;
    payment?: "return" | "cancelled";
  }): string =>
    `/basket/placed/${encodeURIComponent(p.orderNo)}${p.payment ? `?payment=${p.payment}` : ""}`,
  portalQuoteLink: (p: { token: string; quoteNo?: string | null }): string =>
    `/quote/${encodeURIComponent(p.token)}${p.quoteNo ? `?quote_no=${encodeURIComponent(p.quoteNo)}` : ""}`,
  portalOrderLink: (p: { token: string }): string =>
    `/order/${encodeURIComponent(p.token)}`,
  portalProofLink: (p: { token: string }): string =>
    `/proof/${encodeURIComponent(p.token)}`,

  customerPortalAccess: (): string => "/customer-portal",

  /** A purchase order in the supplier portal; `linkToken` puts an emailed link's token in the #fragment. */
  supplierPurchaseOrder: (p: {
    purchaseOrderNo: string;
    linkToken?: string | null;
  }): string =>
    `/po/${encodeURIComponent(p.purchaseOrderNo)}${p.linkToken ? `#${encodeURIComponent(p.linkToken)}` : ""}`,
  supplierOrders: (): string => "/orders",
  /** A quote request in the supplier portal; `linkToken` puts an emailed link's token in the #fragment. */
  supplierQuoteRequest: (p: {
    supplierQuoteNo: string;
    linkToken?: string | null;
  }): string =>
    `/quote-requests/${encodeURIComponent(p.supplierQuoteNo)}${p.linkToken ? `#${encodeURIComponent(p.linkToken)}` : ""}`,
  supplierQuoteRequests: (): string => "/quote-requests",
  supplierInvoices: (): string => "/invoices",
  supplierAccount: (): string => "/account",
  supplierCompany: (): string => "/company",
  /** The open assessment, or a past one read-only by `id`; `section` opens one of its sections. */
  supplierAssessment: (p?: { id?: string; section?: string }): string => {
    const search = new URLSearchParams();
    if (p?.id) search.set("id", p.id);
    if (p?.section) search.set("section", p.section);
    const query = search.toString();
    return query ? `/company/assessment?${query}` : "/company/assessment";
  },
  /** The pick list: a warehouse (when the supplier runs several) and a tab, both optional. */
  supplierPicks: (p?: {
    warehouseId?: string | null;
    tab?: "to_pick" | "despatched_today" | "all" | null;
  }): string => {
    const query = new URLSearchParams();
    if (p?.warehouseId) query.set("warehouse", p.warehouseId);
    if (p?.tab && p.tab !== "to_pick") query.set("tab", p.tab);
    const qs = query.toString();
    return qs ? `/picks?${qs}` : "/picks";
  },
  supplierPick: (p: { pickId: string }): string =>
    `/picks/${encodeURIComponent(p.pickId)}`,
  /** The goods-in list: a warehouse (when the supplier operates several) and a tab, both optional. */
  supplierGoodsIn: (p?: {
    warehouseId?: string | null;
    tab?: "due" | "received_today" | "all" | null;
  }): string => {
    const query = new URLSearchParams();
    if (p?.warehouseId) query.set("warehouse", p.warehouseId);
    if (p?.tab && p.tab !== "due") query.set("tab", p.tab);
    const qs = query.toString();
    return qs ? `/goods-in?${qs}` : "/goods-in";
  },
  /** `returnTo` is a path in the supplier portal to come back to once signed in. */
  supplierSignIn: (p?: { returnTo?: string | null }): string =>
    p?.returnTo
      ? `/sign-in?return_to=${encodeURIComponent(p.returnTo)}`
      : "/sign-in",
} as const;

/** Every route id in the manifest. */
export type RouteId = keyof typeof routes;

/**
 * Is the feature that owns `routeId` on for the signed-in user?
 *
 * The single question both gating levers ask. The client route guard uses it
 * to decide between the page and the not-found page; any component rendering
 * a link INTO another feature uses it to decide whether to render the
 * affordance at all — an enabled feature may link to a disabled one, and left
 * alone those become dead-end buttons.
 *
 * Prefer this over `isFeatureEnabled(...)` at a link site. It derives the
 * answer from the same manifest entry the router uses, so an affordance and
 * its destination cannot disagree about which feature they belong to, and
 * `RouteId` is a compile-time union so a typo does not silently hide a
 * working link.
 *
 * Note this is a **presentation** check. It hides what the user cannot reach;
 * it does not protect anything. The API-side `require_feature` on the router
 * mount is what actually refuses — see
 * docs/features/staging-environment/05_production_surface_spec.md §5.3.
 */
export function isRouteEnabled(routeId: RouteId): boolean {
  return isFeatureEnabled(routes[routeId].feature);
}

/**
 * Routes reachable WITHOUT user authentication. These are capability-URL
 * gated — the token in the path or `?key=` query param IS the credential,
 * validated server-side — so the web shell must NOT redirect them to the
 * Entra sign-in flow. Keep in sync with the top-level routes registered
 * outside the dashboard shell in `apps/frontend/src/app/routes.tsx`.
 */
const PUBLIC_ROUTE_PATHS: readonly string[] = [
  routes.sharedPreview.path,
  routes.wallboard.path,
  routes.exeterBoard.path,
  routes.shopfloorKiosk.path,
  // Portal paths (portalSignIn/portalOrders/...) are deliberately NOT here:
  // the portal moved to its own app (apps/customer-portal) on its own
  // hostname with its own auth model (brief §6, §14), so they are never
  // served on the staff origin this gate protects.
];

/**
 * Public routes whose capability credential rides in the URL itself — an
 * opaque token in the path (`/previews/:shareToken`)
 * or a `?key=` query param (`/wallboard`, `/exeter`). Frontend telemetry
 * must never initialise on these paths: Faro captures full page URLs and
 * query params, which would ship the credential to the log store. The
 * shop-floor kiosk is public but header-authed (token never in the URL),
 * so it is deliberately absent — its error capture stays on.
 */
const URL_CREDENTIAL_ROUTE_PATHS: readonly string[] = [
  routes.sharedPreview.path,
  routes.wallboard.path,
  routes.exeterBoard.path,
];

/**
 * True when `pathname` matches a public (unauthenticated, capability-URL
 * gated) route. The web shell auth provider uses this to skip the Entra
 * gate so the capability token in the URL can be honoured.
 *
 * Matching ignores the query string and a trailing slash; a `:param`
 * template segment matches any single non-empty path segment.
 */
export function isPublicRoutePath(pathname: string): boolean {
  return matchesRouteTemplates(pathname, PUBLIC_ROUTE_PATHS);
}

/**
 * True when `pathname` matches a route whose credential is carried in the
 * URL (see `URL_CREDENTIAL_ROUTE_PATHS`). Used by the Faro bootstrap to
 * refuse telemetry init on these surfaces.
 */
export function isUrlCredentialRoutePath(pathname: string): boolean {
  return matchesRouteTemplates(pathname, URL_CREDENTIAL_ROUTE_PATHS);
}

function matchesRouteTemplates(
  pathname: string,
  templates: readonly string[],
): boolean {
  const path = pathname.replace(/\/+$/, "") || "/";
  return templates.some((template) => {
    const pattern =
      "^" +
      template
        .replace(/\/+$/, "")
        .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        .replace(/:[^/]+/g, "[^/]+") +
      "$";
    return new RegExp(pattern).test(path);
  });
}
